/**
 * One Test it conversation (P-2.11): a thread, the price of each message and the one send path.
 *
 * Extracted from `pages/TestPage.vue` so the page and the Check screen's "Try it before customers
 * do" drawer are the SAME rehearsal: the same session id per chat, the same charge (every message
 * is priced, the first of a session asks once, O-11), the same refusal sentences and the same
 * turn records. What differs is only what rides along on each message (`extras`): the page adds
 * the wording under test, the drawer adds the draft being rehearsed.
 */
import { computed, ref } from 'vue';

import { useWasloI18n } from '../i18n.js';
import { useAiConfirm } from './useAiConfirm.js';
import { wasloApi, WASLO_ERROR_CODES, withConfirm } from '../api/client.js';

/**
 * The five spending refusals, each with its own sentence (W-02): the server says the same thing
 * about all five, in English. Anything else keeps the server's own sentence.
 */
const REFUSAL_COPY = {
  [WASLO_ERROR_CODES.TEST_BUDGET_USED]: 'WASLO.TEST.REFUSED.TEST_BUDGET_USED',
  [WASLO_ERROR_CODES.SPEND_LIMIT_DAY]: 'WASLO.TEST.REFUSED.SPEND_LIMIT_DAY',
  [WASLO_ERROR_CODES.SPEND_LIMIT_MONTH]: 'WASLO.TEST.REFUSED.SPEND_LIMIT_MONTH',
  [WASLO_ERROR_CODES.SPEND_LIMIT_PLATFORM]:
    'WASLO.TEST.REFUSED.SPEND_LIMIT_PLATFORM',
  [WASLO_ERROR_CODES.OUT_OF_CREDITS]: 'WASLO.TEST.REFUSED.OUT_OF_CREDITS',
};

/** The copy key for one of the five spending refusals, or `''` for everything else. */
export const refusalKey = error => {
  const key = REFUSAL_COPY[error?.code];
  return typeof key === 'string' ? key : '';
};

/**
 * A fresh id per chat, the only thing tying its messages together (the thread lives in the
 * sidecar under it). `crypto.randomUUID` is missing on insecure origins, hence the fallback.
 */
export const newSessionId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * @param {{
 *   accountId: import('vue').Ref<number|null>,
 *   extras?: (turn: { first: boolean }) => object,
 *   onReply?: (reply: object) => void,
 *   onError?: (error: unknown) => void,
 * }} options `extras` is called once per message and spread into its body.
 */
export const useTestSession = ({
  accountId,
  extras = () => ({}),
  onReply = () => {},
  onError = () => {},
}) => {
  const { t } = useWasloI18n();
  // P-5.12 + O-11: acknowledged once per session, priced on every message.
  const charge = useAiConfirm({ accountId, op: 'test_message', session: true });

  const sessionId = ref(newSessionId());
  const turns = ref([]);
  const isSending = ref(false);
  /** P-2.11: what "Fetch a sample now" did, for the line under the thread. */
  const sampleNote = ref('');
  const isFetchingSample = ref(false);
  let nextTurnId = 0;

  const sendLabel = computed(() => charge.label(t('WASLO.TEST.COMPOSER.SEND')));

  /** What the owner reads when a turn is refused (see `REFUSAL_COPY`). */
  const refusalText = error => {
    const key = refusalKey(error);
    if (key) return t(key);
    if (error?.code === WASLO_ERROR_CODES.MALFORMED) {
      return t('WASLO.COMMON.ERROR_BODY');
    }
    return error?.message || t('WASLO.COMMON.ERROR_BODY');
  };

  /**
   * One message. `choice` is a tapped option's `{ id, title }`, sent as a tap carries it.
   * Resolves `'cancelled'` when the owner cancelled the price dialog (the turn is gone from the
   * thread), `'done'` otherwise, whether the turn worked or not.
   */
  const sendTurn = async (message, choice = null) => {
    nextTurnId += 1;
    const id = nextTurnId;
    const first = turns.value.length === 0;
    turns.value = [...turns.value, { id, question: message, state: 'sending' }];
    isSending.value = true;
    const payload = {
      sessionId: sessionId.value,
      message,
      ...(choice ? { choice } : {}),
      ...extras({ first }),
    };
    const settle = patch => {
      turns.value = turns.value.map(turn =>
        turn.id === id ? { ...turn, ...patch } : turn
      );
    };
    try {
      const outcome = await charge.confirmAndRun({
        run: credits =>
          wasloApi.sendTestMessage(
            accountId.value,
            payload,
            undefined,
            withConfirm(credits)
          ),
      });
      if (outcome.cancelled) {
        turns.value = turns.value.filter(turn => turn.id !== id);
        return 'cancelled';
      }
      const reply = outcome.result;
      settle({
        state: 'ok',
        text: reply.text,
        handoff: reply.handoff,
        trace: reply.trace,
        menu: reply.menu ?? null,
        choices: reply.choices ?? null,
        attachment: reply.attachment ?? null,
      });
      onReply(reply);
    } catch (error) {
      settle({ state: 'error', error: refusalText(error) });
      onError(error);
    } finally {
      isSending.value = false;
    }
    return 'done';
  };

  /** A new chat: a new id and an empty thread. The acknowledgement stays for this visit. */
  const restart = () => {
    sessionId.value = newSessionId();
    turns.value = [];
    sampleNote.value = '';
  };

  /**
   * P-2.11 "Fetch a sample now": one real test call to the owner's system, pressed by her, through
   * the connection's existing test route (which keeps the answer as the sample). No AI call.
   * `sampleNote` says what happened; the next message uses the sample.
   */
  const fetchSample = async connectionId => {
    if (isFetchingSample.value) return;
    isFetchingSample.value = true;
    sampleNote.value = '';
    try {
      const answer = await wasloApi.tryConnection(
        accountId.value,
        connectionId,
        {}
      );
      sampleNote.value = t(
        answer?.ok === true
          ? 'WASLO.TEST.TRACE.FETCHED'
          : 'WASLO.TEST.TRACE.FETCH_FAILED'
      );
    } catch {
      sampleNote.value = t('WASLO.TEST.TRACE.FETCH_FAILED');
    } finally {
      isFetchingSample.value = false;
    }
  };

  return {
    charge,
    sessionId,
    turns,
    isSending,
    sendLabel,
    receiptText: charge.receiptText,
    sendTurn,
    restart,
    refusalText,
    sampleNote,
    isFetchingSample,
    fetchSample,
  };
};

export default useTestSession;
